/**
 * Tax Payment Dashboard wire types (EPN-255). These mirror, field for field, the contracts in
 * `EPN.TaxCompliance.Api/Features/TaxPaymentDashboard/TaxPaymentDashboardContracts.cs` (camelCase on the wire; enum values are sent as their names; a
 * `DateOnly` is `yyyy-MM-dd`; a `DateTime` is an ISO-8601 UTC instant). Nothing here is invented: a field with no backend source yet (Process No Later Than,
 * Payroll Processor) is `null` on the wire and the screen shows a dash. See docs/tax-payment-dashboard.md.
 */

/** The eight primary statuses (SRS 6). */
export type DashboardStatus =
  | 'PendingPayment' | 'PaymentProcessing' | 'Paid' | 'PaidOutsideEscochex' | 'InsufficientFunds' | 'Failed' | 'NeedsReview' | 'Overdue';

export const DASHBOARD_STATUSES: readonly DashboardStatus[] = [
  'Overdue', 'InsufficientFunds', 'Failed', 'NeedsReview', 'PendingPayment', 'PaymentProcessing', 'Paid', 'PaidOutsideEscochex',
];

/** The SRS tabs (section 15). */
export type DashboardTab = 'All' | 'Pending' | 'Processing' | 'Paid' | 'NeedsAttention';

/** Why a payment is in its status when the status alone does not say (a reason, never a ninth status). `None` when there is nothing to add. */
export type DashboardReason =
  | 'None' | 'InsufficientFundsKotapayManual' | 'InsufficientFundsPartnerReturn' | 'FileRejected' | 'ReturnReasonNotMapped' | 'RecordsConflict'
  | 'OutsidePaymentConflictsWithPayment';

export type PaymentPartner = 'NatPay' | 'Kotapay';

export interface DashboardRow {
  id: string;
  companyId: string;
  formCode: string;
  agency: string;
  jurisdiction: string;
  periodEndDate: string;
  frequency: string;
  dueDate: string;
  /** Null until Process No Later Than has a source (BACKEND GAP). */
  processNoLaterThanUtc: string | null;
  amount: number;
  status: DashboardStatus;
  /** For an Overdue payment, the status it had before the legal due date passed. */
  statusBeforeOverdue: DashboardStatus | null;
  reason: DashboardReason;
  paymentPartner: PaymentPartner | null;
  initiatedAtUtc: string | null;
  paidAtUtc: string | null;
  datePaid: string | null;
  /** Null until the Payroll Processor has a source (BACKEND GAP). */
  payrollProcessor: string | null;
  isSimulated: boolean;
}

export interface DashboardSummary {
  pendingPayments: number;
  processing: number;
  failedNeedsAttention: number;
  /** Null while Process No Later Than has no source: the card shows a dash, never a made-up zero. */
  processingDeadlinesPassed: number | null;
  totalAmountDue: number;
}

export interface DashboardCounts {
  all: number;
  pending: number;
  processing: number;
  paid: number;
  needsAttention: number;
}

export interface DashboardResponse {
  items: DashboardRow[];
  total: number;
  page: number;
  pageSize: number;
  countsByTab: DashboardCounts;
  summary: DashboardSummary;
  queue: DashboardRow[];
  isPlatformView: boolean;
  simulated: boolean;
  simulationNote: string;
}

/** What the list asks for. `companyId`/`allCompanies` are only sent on the platform route; the company route refuses them. */
export interface DashboardQuery {
  tab?: DashboardTab;
  status?: DashboardStatus;
  formCode?: string;
  dueFrom?: string;
  dueTo?: string;
  page?: number;
  pageSize?: number;
  companyId?: string;
}

/** Which door a read goes through: the caller's own company, or the Super Admin's cross-company platform route. */
export type TaxPaymentScope = 'company' | 'platform';

export type DashboardActionCode = 'RecordOutsidePayment' | 'VoidOutsidePaymentRecord' | 'MarkInsufficientFunds' | 'RetryPayment';

/** One thing the drawer may offer, decided on the server; `reason` says why it is unavailable. */
export interface DashboardAction {
  code: DashboardActionCode | string;
  eligible: boolean;
  reason: string | null;
}

export interface OutsideDocument {
  id: string;
  displayName: string;
  contentType: string;
  sizeBytes: number;
}

export type OutsidePaymentMethod = 'Check' | 'AchCredit' | 'Eftps' | 'AgencyOnline' | 'Other';

/** The method list from the contract (`OutsidePaymentMethod`), with the wording the Record Outside Payment form shows. */
export const OUTSIDE_PAYMENT_METHODS: ReadonlyArray<{ value: OutsidePaymentMethod; label: string }> = [
  { value: 'Check', label: 'Check' },
  { value: 'AchCredit', label: 'ACH Credit' },
  { value: 'Eftps', label: 'EFTPS' },
  { value: 'AgencyOnline', label: 'Agency Online Payment' },
  { value: 'Other', label: 'Other' },
];

export interface OutsideRecord {
  id: string;
  paymentDate: string;
  amount: number;
  method: OutsidePaymentMethod | string;
  referenceNumber: string | null;
  notes: string | null;
  recordedByRole: string;
  recordedAtUtc: string;
  status: 'Active' | 'Voided';
  voidReason: string | null;
  voidedAtUtc: string | null;
  documents: OutsideDocument[];
}

export interface InsufficientFundsEvidence {
  source: string;
  dateIdentified: string;
  reasonReference: string;
  /** Null unless the caller holds the comment permission. */
  internalNote: string | null;
  recordedByRole: string;
  recordedAtUtc: string;
}

export interface TimelineEntry {
  occurredAtUtc: string;
  eventType: string;
  actorRole: string | null;
  byTheSystem: boolean;
  previousValue: string | null;
  newValue: string | null;
  reason: string | null;
  isSimulated: boolean;
}

export interface DashboardNote {
  id: string;
  text: string;
  authorRole: string;
  createdAtUtc: string;
}

export interface DashboardDetail {
  row: DashboardRow;
  actions: DashboardAction[];
  outsideRecord: OutsideRecord | null;
  insufficientFunds: InsufficientFundsEvidence | null;
  timeline: TimelineEntry[];
  /** Null unless the caller holds the comment permission (the server never sends internal notes otherwise). */
  notes: DashboardNote[] | null;
  inquiryNote: string | null;
  simulationNote: string;
}

/** The record-outside form. The files travel in the same multipart request; the server stores every file before it saves the record. */
export interface RecordOutsideInput {
  paymentDate: string;
  amount: number;
  method: OutsidePaymentMethod;
  referenceNumber: string;
  notes: string;
  acknowledged: boolean;
  files: File[];
}

export interface VoidOutsideInput { reason: string; confirmed: boolean }

export interface InsufficientFundsInput { dateIdentified: string; reasonReference: string; internalNote: string; acknowledged: boolean }

/** A document returned by the authorized download endpoint. The raw storage key never reaches the browser. */
export interface DownloadedDocument { blob: Blob; fileName: string }

export const statusLabels: Record<DashboardStatus, string> = {
  PendingPayment: 'Pending Payment',
  PaymentProcessing: 'Payment Processing',
  Paid: 'Paid',
  PaidOutsideEscochex: 'Paid Outside Escochex',
  InsufficientFunds: 'Insufficient Funds',
  Failed: 'Failed',
  NeedsReview: 'Needs Review',
  Overdue: 'Overdue',
};

export function statusLabel(status: string): string {
  return statusLabels[status as DashboardStatus] ?? status;
}

export const tabLabels: Record<DashboardTab, string> = {
  All: 'All Payments', Pending: 'Pending', Processing: 'Processing', Paid: 'Paid', NeedsAttention: 'Needs Attention',
};

/** The tab a status belongs to (SRS 15). Mirrors `TaxPaymentStatusResolver.TabOf`; used only to label a row, never to filter (the server filters). */
export function tabOfStatus(status: DashboardStatus): Exclude<DashboardTab, 'All'> {
  switch (status) {
    case 'PendingPayment': return 'Pending';
    case 'PaymentProcessing': return 'Processing';
    case 'Paid':
    case 'PaidOutsideEscochex': return 'Paid';
    default: return 'NeedsAttention';
  }
}
